// God → Gateway → Merchant access: switch payins/payouts on per merchant, set Auto Pay
// routing and limits, and adjust gateway balances (ledgered, shown to the merchant).
// SuperCash charges no fees — ProtonPay's charges stay in its own dashboard.
import { useState } from "react";
import { toast } from "sonner";
import { Scale, ShieldCheck, SlidersHorizontal, Store, Wallet, Zap } from "lucide-react";
import { god } from "../../godApi";
import { useDebounced, usePagedList, useSyncFilter } from "../../../components/sc/ui";
import {
  Avatar, Button, Callout, Cell, Dialog, Empty, Field, Input, Page, Pagination, Panel, PanelHero, Pill, Rows,
  SearchInput, Section, Table, Toggle, Toolbar, inr,
} from "../../kit";

const onOff = (on) => <Pill tone={on ? "green" : "gray"} dot>{on ? "On" : "Off"}</Pill>;
const route = (g) => (g.p2g_auto ? `≥ ${inr(g.p2g_auto_from || 0)} → P2G` : "Off");

export default function GwMerchants() {
  const [qInput, setQInput] = useState("");
  const q = useDebounced(qInput);
  const list = usePagedList((p) => god.gwMerchants(p), { q: "" }, 25);
  useSyncFilter(list.setFilter, "q", q);
  const [edit, setEdit] = useState(null);

  return (
    <Page>
      <Section title="Merchant gateway access" icon={ShieldCheck} pad={false}
        sub={`${list.data.total.toLocaleString("en-IN")} merchants`}>
        <Toolbar>
          <SearchInput value={qInput} onChange={setQInput} placeholder="Search merchant by name…" testId="god-gw-merchants-search" />
        </Toolbar>
        {list.error && <div className="gwm-pad"><Callout tone="red" title="Couldn't load">{list.error}</Callout></div>}
        <Table testId="god-gw-merchants" rows={list.data.items} loading={list.loading} onRowClick={setEdit} activeId={edit?.id}
          empty={<Empty icon={Store} title={q ? "No merchants match" : "No merchants yet"}>{q ? "Try another name." : "Merchants appear here once they sign up."}</Empty>}
          columns={[
            { key: "name", label: "Merchant", render: (m) => (
              <span className="gwm-name">
                <Avatar name={m.name} />
                <Cell main={<>{m.name}{m.suspended && <> <Pill tone="red">Suspended</Pill></>}</>} sub={<span className="k-mono">{m.id}</span>} />
              </span>
            ) },
            { key: "pin", label: "Payin", render: (m) => onOff(m.gateway.payin_enabled) },
            { key: "pout", label: "Payout", render: (m) => onOff(m.gateway.payout_enabled) },
            { key: "route", label: "Auto Pay routing", hideSm: true, render: (m) => <span className="k-mono">{route(m.gateway)}</span> },
            { key: "bal", label: "Balance", align: "right", render: (m) => <b>{inr(m.gateway.balance)}</b> },
          ]} />
        <Pagination page={list.data.page} pages={list.data.pages} total={list.data.total} size={list.size} onPage={list.setPage} onSize={list.setSize} />
      </Section>

      {edit && <MerchantEditor key={edit.id} m={edit} onClose={() => setEdit(null)} onSaved={list.load} />}

      <style>{`
        .gwm-name { display: inline-flex; align-items: center; gap: 10px; min-width: 0; }
        .gwm-pad { padding: 12px 18px 0; }
        .gwm-note { margin: 12px 0 0; font-size: 13px; line-height: 1.5; color: #5b6170; }
      `}</style>
    </Page>
  );
}

function MerchantEditor({ m, onClose, onSaved }) {
  const g = m.gateway;
  const [f, setF] = useState({ ...g });
  const [adj, setAdj] = useState({ amount: "", note: "" });
  const [bal, setBal] = useState(g.balance);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e?.target ? e.target.value : e }));
  const num = (v) => (v === "" || v == null ? null : Number(v));

  const save = async () => {
    setBusy(true);
    try {
      await god.gwSaveMerchant(m.id, {
        payin_enabled: f.payin_enabled, payout_enabled: f.payout_enabled,
        p2g_auto: !!f.p2g_auto, p2g_auto_from: num(f.p2g_auto_from),
        payin_min: num(f.payin_min), payin_max: num(f.payin_max), payout_min: num(f.payout_min), payout_max: num(f.payout_max),
      });
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const askAdjust = () => {
    const amount = Number(adj.amount);
    if (!amount || adj.note.trim().length < 3) return toast.error("Amount (±) and a note are required");
    setConfirm({ amount, note: adj.note.trim() });
  };

  const adjust = async () => {
    setBusy(true);
    try {
      const r = await god.gwAdjust(m.id, { amount: confirm.amount, note: confirm.note });
      setBal(r.balance);
      setAdj({ amount: "", note: "" });
      setConfirm(null);
      toast.success(`Balance now ${inr(r.balance)}`);
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const credit = confirm && confirm.amount > 0;

  return (
    <Panel title={m.name} sub="Gateway access" onClose={onClose}
      actions={<Button variant="primary" size="sm" disabled={busy} onClick={save}>Save</Button>}>
      <PanelHero kicker="Gateway balance" title={inr(bal)}
        meta={<><span>Payin</span>{onOff(f.payin_enabled)}<span>Payout</span>{onOff(f.payout_enabled)}{m.suspended && <Pill tone="red">Suspended</Pill>}</>}
        reference={m.id} />

      <Section title="Access" icon={ShieldCheck}>
        <Rows items={[
          ["Payins", <Toggle testId="god-gw-m-payin" label="Payins" on={!!f.payin_enabled} onChange={set("payin_enabled")} />],
          ["Payouts", <Toggle testId="god-gw-m-payout" label="Payouts" on={!!f.payout_enabled} onChange={set("payout_enabled")} />],
        ]} />
      </Section>

      <Section title="Auto Pay routing" icon={Zap} sub="Real amounts only — no SuperCash charges; ProtonPay's charges stay in its dashboard.">
        <Rows items={[
          ["Route by amount", <Toggle testId="god-gw-m-route" label="Route by amount" on={!!f.p2g_auto} onChange={set("p2g_auto")} />],
        ]} />
        <div className="k-form" style={{ marginTop: 10 }}>
          <Field label="Use Auto Pay (P2G) from ₹">
            <Input type="number" min="0" data-testid="god-gw-m-route-from" disabled={!f.p2g_auto} value={f.p2g_auto_from ?? ""} onChange={set("p2g_auto_from")} placeholder="e.g. 300" />
          </Field>
        </div>
        <p className="gwm-note">
          {f.p2g_auto
            ? `Orders of ${inr(num(f.p2g_auto_from) || 0)} or more pay through Auto Pay. Smaller ones use the P2M / P2P mode the merchant chose. If Auto Pay is unavailable, the merchant's own mode is used.`
            : "Off: every order uses the mode the merchant chose."}
        </p>
      </Section>

      <Section title="Limits" icon={SlidersHorizontal} sub="In ₹. Leave blank for no limit.">
        <div className="k-form">
          <Field label="Payin min"><Input type="number" min="0" value={f.payin_min ?? ""} onChange={set("payin_min")} /></Field>
          <Field label="Payin max"><Input type="number" min="0" value={f.payin_max ?? ""} onChange={set("payin_max")} /></Field>
          <Field label="Payout min"><Input type="number" min="0" value={f.payout_min ?? ""} onChange={set("payout_min")} /></Field>
          <Field label="Payout max"><Input type="number" min="0" value={f.payout_max ?? ""} onChange={set("payout_max")} /></Field>
        </div>
        <div className="k-actions" style={{ marginTop: 14 }}>
          <Button variant="primary" disabled={busy} onClick={save}>Save changes</Button>
        </div>
      </Section>

      <Section title="Manual balance adjustment" icon={Wallet} sub="Recorded in the merchant's statement. A negative amount debits.">
        <div className="k-form">
          <Field label="Amount ₹ (±)">
            <Input type="number" step="0.01" value={adj.amount} onChange={(e) => setAdj((a) => ({ ...a, amount: e.target.value }))} placeholder="-250.00" />
          </Field>
          <Field label="Note (shown to merchant)">
            <Input value={adj.note} onChange={(e) => setAdj((a) => ({ ...a, note: e.target.value }))} placeholder="Settlement top-up" />
          </Field>
        </div>
        <div className="k-actions" style={{ marginTop: 14 }}>
          <Button icon={Scale} disabled={busy} onClick={askAdjust}>Apply adjustment</Button>
        </div>
      </Section>

      {confirm && (
        <Dialog title={credit ? "Credit balance?" : "Debit balance?"} onClose={() => setConfirm(null)} footer={<>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button variant={credit ? "primary" : "danger"} disabled={busy} onClick={adjust}>
            {busy ? "Applying…" : `${credit ? "Credit" : "Debit"} ${inr(Math.abs(confirm.amount))}`}
          </Button>
        </>}>
          <p style={{ margin: 0 }}>
            {credit ? "Credit" : "Debit"} <b>{inr(Math.abs(confirm.amount))}</b> {credit ? "to" : "from"} <b>{m.name}</b>? This is recorded in their statement and can't be undone (post an opposite adjustment to reverse it).
          </p>
          <Rows items={[
            ["Current balance", inr(bal)],
            ["New balance", inr(Math.round((bal + confirm.amount) * 100) / 100)],
            ["Note", confirm.note],
          ]} />
        </Dialog>
      )}
    </Panel>
  );
}
